import qs from 'qs';

import { useMutation, useQuery } from '../../../ReactQuery';
import { get, patch, put } from '../../../redux/api';
import { nuc } from '../../../utils';
import type { PackageLabel } from '../../../../shared/model-typings/packagelabel';
import type { PackageLabelAssignmentWithName } from '../../../../shared/model-typings/packagelabelassignment';
import type { PackageLabelScope } from '../../../../shared/packageLabels';
import type { VersionedLabelOption } from '../../../components/modals/ModalManagePackageLabels/types';

export type PackageLabelResponse = {
  packageLabels: PackageLabel[];
};

function getOrganizationPackageLabels() {
  return get<PackageLabelResponse>('/api/package-labels', true);
}

/**
 * Fetch all of the package labels that an organization has defined.
 */
export function useOrganizationPackageLabels() {
  return useQuery<PackageLabelResponse>({
    queryFn: () => getOrganizationPackageLabels(),
    queryKey: ['packageLabels'],
  });
}

export type PackageLabelAssignmentResponse = {
  packageLabelAssignments: PackageLabelAssignmentWithName[];
};

function getPackageLabelAssignments(packageId: string, scopeId?: string) {
  const filterParams = {
    filters: {
      packageId: nuc(packageId),
      ...(scopeId ? { scopeId: nuc(scopeId) } : {}),
    },
  };
  const params = qs.parse(qs.stringify(filterParams));
  return get<PackageLabelAssignmentResponse>(`/api/package-label-assignments`, true, params);
}

/**
 * Fetch all of the package labels assigned to a dependency.
 */
export function usePackageLabelAssignments(packageId: string, scopeId?: string) {
  return useQuery<PackageLabelAssignmentResponse>({
    queryKey: ['packageLabels', packageId, scopeId],
    queryFn: () => getPackageLabelAssignments(packageId, scopeId),
  });
}

/**
 * PUT to the bulk update endpoint to handle the assignment and un-assignment of labels to a package in a single scope.
 */
function putPackageLabelAssignments(
  packageId: string,
  scope: PackageLabelScope,
  newLabelIds: VersionedLabelOption,
  scopeId?: string,
) {
  // convert the sets to arrays for network transport
  const newLabelIdsArrays = Object.fromEntries(
    Object.entries(newLabelIds).map(([versionKey, labelIds]) => [versionKey, Array.from(labelIds)]),
  );
  return put(`/api/package-label-assignments`, {
    packageId,
    scope,
    newLabelIds: newLabelIdsArrays,
    scopeId,
  });
}

export type UpdatePackageLabelAssignmentsRequest = {
  newLabelIds: VersionedLabelOption;
  scope: PackageLabelScope;
  scopeId?: string;
};
/**
 * Update the labels assigned to a package.
 */
export function useUpdatePackageLabelAssignments(
  packageId: string,

) {
  const updatePackageLabelAssignments = useMutation({
    mutationFn: ({ newLabelIds, scope, scopeId }: UpdatePackageLabelAssignmentsRequest) =>
      putPackageLabelAssignments(packageId, scope, newLabelIds, scopeId),
  });

  return { updatePackageLabelAssignments };
}

export type PatchPackageLabelRequest = {
  labelId: number;
  newLabel: string;
};

function patchPackageLabel(labelId: number, newLabel: string) {
  return patch(`/api/package-labels/${labelId}`, { name: newLabel });
}

/**
 * PATCH a package label to change its name in place across all usages.
 */
export function usePatchPackageLabel() {
  return useMutation({
    mutationFn: ({ labelId, newLabel }: PatchPackageLabelRequest) => patchPackageLabel(labelId, newLabel),
  });
}
